import { useMutation } from '@apollo/client'
import { Box, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useCallback, useRef, useState } from 'react'
import { Alert, Button, TextField } from 'ui/baseComponents'
import { TAG_PATIENTS, UNTAG_PATIENTS } from '../../graphql/tagMutations'

const ManualTagging = ({ tag }) => {
  const [patientIds, setPatientIds] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const [tagPatients] = useMutation(TAG_PATIENTS)
  const [untagPatients] = useMutation(UNTAG_PATIENTS)

  const handleCsvUpload = useCallback((event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result
      if (typeof text !== 'string') return

      const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)

      const hasHeader = lines.length > 0 && isNaN(lines[0])
      const ids = hasHeader ? lines.slice(1) : lines

      setPatientIds(ids.join('\n'))
    }
    reader.onerror = () => {
      setError('Failed to read CSV file')
    }
    reader.readAsText(file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const getIds = useCallback(
    () =>
      patientIds
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    [patientIds],
  )

  const handleTag = useCallback(async () => {
    const ids = getIds()
    if (!ids.length) {
      setError('Enter at least one patient ID')
      return
    }
    setError(null)
    setResult(null)
    try {
      await tagPatients({
        variables: { input: { patientIds: ids, tagId: tag.id } },
      })
      setResult(`Tagged ${ids.length} patient(s)`)
      setPatientIds('')
    } catch (err) {
      setError(
        err.graphQLErrors?.[0]?.message ||
          'Failed to tag patients. Please try again.',
      )
    }
  }, [getIds, tag, tagPatients])

  const handleUntag = useCallback(async () => {
    const ids = getIds()
    if (!ids.length) {
      setError('Enter at least one patient ID')
      return
    }
    setError(null)
    setResult(null)
    try {
      await untagPatients({
        variables: { input: { patientIds: ids, tagId: tag.id } },
      })
      setResult(`Untagged ${ids.length} patient(s)`)
      setPatientIds('')
    } catch (err) {
      setError(
        err.graphQLErrors?.[0]?.message ||
          'Failed to untag patients. Please try again.',
      )
    }
  }, [getIds, tag, untagPatients])

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tag: <strong>{tag.tag}</strong> ({tag.id})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {result}
        </Alert>
      )}

      <TextField
        label="Patient IDs (one per line)"
        value={patientIds}
        onChange={(e) => setPatientIds(e.target.value)}
        multiline
        rows={5}
        fullWidth
        size="small"
        placeholder="Enter one patient ID per line"
      />

      <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
        <input
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleCsvUpload}
        />
        <Button
          type="secondary"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload CSV
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="primary" size="small" onClick={handleTag}>
          Tag Patients
        </Button>
        <Button type="secondary" size="small" onClick={handleUntag}>
          Untag Patients
        </Button>
      </Box>
    </Box>
  )
}

export default ManualTagging
